import { auth, db } from '../lib/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc
} from 'firebase/firestore';

/**
 * Authentication service using Firebase Auth + Firestore
 */
export const authService = {
  /**
   * Sign in user with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Object} - { data, error }
   */
  async signIn(email, password) {
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email?.trim()?.toLowerCase(),
        password
      );
      return { data: credential, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error?.message || 'An unexpected error occurred during sign in.'
        }
      };
    }
  },

  /**
   * Sign up new user (admin only functionality)
   * @param {Object} userData - User registration data
   * @returns {Object} - { data, error }
   */
  async signUp(userData) {
    try {
      const { email, password, full_name, role, employee_id, department } = userData;
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email?.trim()?.toLowerCase(),
        password
      );

      // Create a user profile document in Firestore
      const uid = userCredential?.user?.uid;
      if (uid) {
        const profileRef = doc(db, 'user_profiles', uid);
        await setDoc(profileRef, {
          id: uid,
          email: email?.trim()?.toLowerCase(),
          full_name: full_name || null,
          role: role || 'user',
          employee_id: employee_id || null,
          department: department || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }

      return { data: userCredential, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error?.message || 'An unexpected error occurred during sign up.'
        }
      };
    }
  },

  /**
   * Sign out current user
   * @returns {Object} - { error }
   */
  async signOut() {
    try {
      await firebaseSignOut(auth);
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'An unexpected error occurred during sign out.' } };
    }
  },

  /**
   * Get current session
   * @returns {Object} - { data, error }
   */
  async getSession() {
    try {
      // Firebase does not expose a getSession API the same way; return currentUser
      const currentUser = auth?.currentUser || null;
      return { data: { user: currentUser }, error: null };
    } catch (error) {
      return { data: null, error: { message: 'Failed to retrieve session information.' } };
    }
  },

  /**
   * Get user profile from user_profiles table
   * @param {string} userId - User ID
   * @returns {Object} - { data, error }
   */
  async getUserProfile(userId) {
    try {
      if (!userId) {
        return { data: null, error: { message: 'User ID is required' } };
      }

      const profileRef = doc(db, 'user_profiles', userId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return { data: null, error: { message: 'User profile not found' } };
      return { data: snap.data(), error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: 'Failed to retrieve user profile.' 
        } 
      };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object} updates - Profile updates
   * @returns {Object} - { data, error }
   */
  async updateUserProfile(userId, updates) {
    try {
      if (!userId) {
        return { data: null, error: { message: 'User ID is required' } };
      }

      const profileRef = doc(db, 'user_profiles', userId);
      const payload = { ...updates, updated_at: new Date().toISOString() };
      await updateDoc(profileRef, payload);
      const updatedSnap = await getDoc(profileRef);
      return { data: updatedSnap.data(), error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: 'Failed to update user profile.' 
        } 
      };
    }
  },

  /**
   * Check if user has specific role
   * @param {string} userId - User ID
   * @param {string} requiredRole - Required role
   * @returns {Object} - { hasRole, error }
   */
  async checkUserRole(userId, requiredRole) {
    try {
      if (!userId || !requiredRole) {
        return { hasRole: false, error: { message: 'User ID and role are required' } };
      }

      const profileRef = doc(db, 'user_profiles', userId);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) return { hasRole: false, error: { message: 'User not found' } };
      const role = snap.data()?.role;
      return { hasRole: role === requiredRole, error: null };
    } catch (error) {
      return { 
        hasRole: false, 
        error: { 
          message: 'Failed to check user role.' 
        } 
      };
    }
  },

  /**
   * Get user permissions for specific module
   * @param {string} role - User role
   * @param {string} module - Module name
   * @returns {Object} - { permissions, error }
   */
  async getUserPermissions(role, module) {
    try {
      if (!role || !module) {
        return { permissions: null, error: { message: 'Role and module are required' } };
      }

      // Query role_permissions collection for matching document
      const rpQuery = query(collection(db, 'role_permissions'), where('role', '==', role), where('module', '==', module));
      const snaps = await getDocs(rpQuery);
      if (snaps.empty) {
        const defaultPermissions = {
          can_view: false,
          can_create: false,
          can_edit: false,
          can_delete: false,
          can_approve: false
        };
        return { permissions: defaultPermissions, error: null };
      }
      // Return first matching doc
      const docData = snaps.docs[0].data();
      return { permissions: docData, error: null };
    } catch (error) {
      return { 
        permissions: null, 
        error: { 
          message: 'Failed to retrieve user permissions.' 
        } 
      };
    }
  },

  /**
   * Reset password (sends reset email)
   * @param {string} email - User's email
   * @returns {Object} - { data, error }
   */
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email?.trim()?.toLowerCase(), { url: `${window?.location?.origin}/login` });
      return { data: null, error: null };
    } catch (error) {
      let message = 'Failed to send password reset email.';
      if (error.code === 'auth/user-not-found') {
        message = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address format.';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many requests. Please wait a few minutes before trying again.';
      } else if (error?.message) {
        message = error.message;
      }
      return { data: null, error: { message } };
    }
  },

  /**
   * Change password for the currently signed-in user
   * @param {string} currentPassword - Current password for reauthentication
   * @param {string} newPassword - New password
   * @returns {Object} - { error }
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        return { error: { message: 'No active session. Please sign in again.' } };
      }

      if (!newPassword || newPassword.length < 6) {
        return { error: { message: 'New password must be at least 6 characters long.' } };
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      return { error: null };
    } catch (error) {
      let message = 'Failed to update password.';
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'The current password you entered is incorrect.';
      } else if (error.code === 'auth/weak-password') {
        message = 'The new password is too weak. Please use a stronger password.';
      } else if (error.code === 'auth/requires-recent-login') {
        message = 'This action requires recent authentication. Please sign out and sign in again.';
      } else if (error?.message) {
        message = error.message;
      }
      return { error: { message } };
    }
  }
};

export default authService;